import { useState, useRef, useEffect } from 'react';
import EditorToolbarFun from './EditorToolbarFun';
import { useDispatch } from 'react-redux';
import { editBlock } from '../../Store/api/create';
import { useSelector } from 'react-redux';
import { bookActions } from '../../Store/entities/book';
import { toast } from 'react-toastify';
import { ModalPortal } from '../ModalPortal';

export const TextEditorFun = ({
  setEditorState = () => {},
  editorState,
  bookId,
  setBook,
  setShowBlock,
  editingOptions,
  setSelectOption,
  editableElementRef,
}) => {
  const modalRef = useRef(null);
  const [url, setUrl] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [savedSelection, setSelection] = useState(null);
  const { book } = useSelector((state) => state.entities.book);
  const bookRef = useRef(book);

  const dispatch = useDispatch();
  const elementRef = useRef(null);

  useEffect(() => {
    const handleSelectionChange = () => {
      let font = 'Lora';
      let align = 'left';
      let isBulleted = false;
      let isOrdered = false;

      const selection = document.getSelection();
      const selectedNode = selection?.anchorNode;

      if (selectedNode) {
        const parentElement =
          selectedNode.nodeType === Node.TEXT_NODE ? selectedNode.parentElement : selectedNode;

        font = window.getComputedStyle(parentElement).fontFamily;
        align = window.getComputedStyle(parentElement).textAlign?.replace('start', 'left');

        // 🔍 Traverse up the DOM to check if inside a UL (bulleted list) or OL (ordered list)
        let node = parentElement;
        while (node && node !== document) {
          if (node.tagName === 'UL') {
            isBulleted = true;
            break;
          }
          if (node.tagName === 'OL') {
            isOrdered = true;
            break;
          }
          node = node.parentElement;
        }
      }

      setEditorState((prevState) => ({
        ...prevState,
        fontFamily: font,
        formatting: {
          ...prevState.formatting,
          bold: document.queryCommandState('bold'),
          italic: document.queryCommandState('italic'),
          underline: document.queryCommandState('underline'),
          bullets: isBulleted, // ✅ Use traversal result
          orders: isOrdered, // ✅ Use traversal result
          link: document.queryCommandState('createLink'),
          align: align,
        },
      }));
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  useEffect(() => {
    bookRef.current = book;
  }, [book]);

  const updateFormatting = (style) => {
    editableElementRef?.current?.focus?.();
    setEditorState((old) => ({
      ...old,
      formatting: { ...old.formatting, [style]: !old.formatting[style] },
    }));
    document.execCommand(style, false, null);
  };
  const isFirstRender = useRef(true);
  const timerId = useRef(null);
  useEffect(() => {
    if (isFirstRender.current) {
      // Skip the first render
      isFirstRender.current = false;
      return;
    }
    // editableElementRef.current.innerHTML = editorState.content
    // eslint-disable-next-line react-hooks/exhaustive-deps
    timerId.current = setTimeout(() => {
      dispatch(
        editBlock({
          onSuccess: (res) => {},
          onError: (err) => {
            toast.error('Failed to update changes.');
          },
          body: { [editingOptions.field]: editorState.content },
          id: editingOptions.elementId,
          route: editingOptions.parentElement,
        })
      );
    }, 500);
    switch (editingOptions.parentElement) {
      case 'chapter': {
        const newChapters = JSON.parse(JSON.stringify(book.chapters));
        newChapters[editingOptions.chapInd][editingOptions.field] =
          editableElementRef.current.innerHTML;
        dispatch(bookActions.setMyBook({ ...book, chapters: newChapters }));
        break;
      }
      case 'section': {
        const newChapters = JSON.parse(JSON.stringify(book.chapters));
        newChapters[editingOptions.chapInd].sections[editingOptions.secInd][editingOptions.field] =
          editableElementRef.current.innerHTML;
        dispatch(bookActions.setMyBook({ ...book, chapters: newChapters }));
        break;
      }
      case 'paragraph': {
        if (!url || !isOpen) {
          const newChapters = JSON.parse(JSON.stringify(book.chapters));
          newChapters[editingOptions.chapInd].sections[editingOptions.secInd].paragraphs[
            editingOptions.parInd
          ][editingOptions.field] = editableElementRef.current.innerHTML;
          dispatch(bookActions.setMyBook({ ...book, chapters: newChapters }));
        }
        break;
      }

      default:
        break;
    }
    return () => {
      clearTimeout(timerId.current);
    };
  }, [
    editorState.content,
    editingOptions.elementId,
    editingOptions.parentElement,
    editingOptions.secInd,
    editingOptions.chapInd,
    editingOptions.field,
    dispatch,
    setBook,
  ]);

  useEffect(() => {
    isFirstRender.current = true;
    // Get the element by ID
    const inputElement = editableElementRef.current;

    if (Object.keys(inputElement).length > 0) {
      inputElement.contentEditable = true;
    } else {
      return;
    }

    setEditorState((old) => ({
      ...old,
      content: inputElement.innerHTML,
      currentState: [],
    }));
    setTimeout(() => {
      inputElement.focus?.();
      setEditorState((old) => ({
        ...old,
        currentState: [inputElement.innerHTML],
        currentStateIndex: 0,
        originalContent: inputElement.innerHTML,
      }));
    }, 300);
    const handleInput = (event) => {
      saveStateF(event);
    };
    let ctrlKeyPressed = false; // Track if Ctrl/Meta key is pressed

    const handleKeyDown = (event) => {
      if (event.key === 'Control' || event.key === 'Meta') {
        ctrlKeyPressed = true;
        return;
      }

      if (event.key?.toLowerCase() === 'tab') {
        event.preventDefault();
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        const indentNode = document.createTextNode('\u00A0\u00A0\u00A0\u00A0');
        range.insertNode(indentNode);
        range.setStartAfter(indentNode);
        range.setEndAfter(indentNode);
        selection.removeAllRanges();
        selection.addRange(range);
        saveStateF(event);
        return;
      }

      if (ctrlKeyPressed) {
        switch (event.key.toLowerCase()) {
          case 'b':
            event.preventDefault();
            document.execCommand('bold');
            editableElementRef.current.focus?.();
            setEditorState((old) => ({
              ...old,
              formatting: {
                ...old.formatting,
                bold: !old.formatting.bold,
              },
            }));
            break;
          case 'i':
            event.preventDefault();
            editableElementRef.current.focus?.();
            setEditorState((old) => ({
              ...old,
              formatting: {
                ...old.formatting,
                italic: !old.formatting.italic,
              },
            }));
            document.execCommand('italic');
            break;
          case 'u':
            event.preventDefault();
            editableElementRef.current.focus?.();
            setEditorState((old) => ({
              ...old,
              formatting: {
                ...old.formatting,
                underline: !old.formatting.underline,
              },
            }));
            document.execCommand('underline');
            break;
          case 'z':
            if (editorState.currentStateIndex <= 0 || editorState?.currentState?.length === 0) {
              return;
            } else undo();
            break;
          default:
            break;
        }
        ctrlKeyPressed = false; // Reset after handling Ctrl+key combination
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === 'Control' || event.key === 'Meta') {
        ctrlKeyPressed = false;
      }
    };

    const handleDrop = (event) => {
      event.preventDefault();
    };
    inputElement.addEventListener?.('input', handleInput);
    inputElement.addEventListener?.('keydown', handleKeyDown);
    inputElement.addEventListener?.('keyup', handleKeyUp); // Add keyup listener
    inputElement.addEventListener?.('drop', handleDrop);
    return () => {
      editableElementRef?.current?.removeEventListener?.('keydown', handleKeyDown);
      editableElementRef?.current?.removeEventListener?.('keyup', handleKeyUp);
    };
  }, [editableElementRef, editableElementRef.current]);

  const createLink = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      let savedSelection = selection.getRangeAt(0).cloneRange(); // Save the range
      setSelection(savedSelection);

      const anchorElement = savedSelection.commonAncestorContainer.parentElement?.closest('a');
      if (anchorElement) {
        setUrl(anchorElement.getAttribute('href') || '');
      }
    }
    setIsOpen(true);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setUrl(value);

    try {
      new URL(value); // Built-in validation
      setIsValid(true);
    } catch (err) {
      setIsValid(false);
    }
  };

  const handleClick = () => {
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection); // Restore the saved range

      if (url && isValid) {
        document.execCommand('createLink', false, url);
      }
      setUrl('');
      setSelection(null); // Clear after use
      setIsOpen(false);
    }
  };

  const handleRemoveLink = () => {
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection); // Restore the saved selection

      document.execCommand('unlink'); // This removes the link at selection

      setUrl('');
      setSelection(null); // Clear after use
      setIsOpen(false);
    }
  };

  const toggleBullets = () => {
    editableElementRef?.current?.focus?.();
    document.execCommand('insertUnorderedList');
    setEditorState((prevState) => ({
      ...prevState,
      formatting: {
        ...prevState.formatting,
        bullets: !prevState.formatting.bullets,
      },
    }));
  };

  const toggleOrders = () => {
    editableElementRef?.current.focus?.();
    document.execCommand('insertOrderedList');
    setEditorState((prevState) => ({
      ...prevState,
      formatting: {
        ...prevState.formatting,
        orders: !prevState.formatting.orders,
      },
    }));
  };

  const undo = () => {
    const { currentState, currentStateIndex } = editorState;
    if (currentStateIndex > 0) {
      const newIndex = currentStateIndex - 1;
      setEditorState({
        ...editorState,
        currentStateIndex: newIndex,
      });
      editableElementRef.current.innerHTML = currentState[newIndex];
    } else {
      editableElementRef.current.innerHTML = editorState.originalContent;
      setEditorState({
        ...editorState,
        currentStateIndex: -1,
        content: editorState.originalContent,
      });
    }
  };

  const redo = () => {
    const { currentState, currentStateIndex } = editorState;
    if (currentStateIndex < currentState.length - 1) {
      const newIndex = currentStateIndex + 1;
      setEditorState({
        ...editorState,
        currentStateIndex: newIndex,
      });
      editableElementRef.current.innerHTML = currentState[newIndex];
    }
  };

  const changeFontSize = (selectedOption) => {
    editableElementRef?.current?.focus?.();
    const fontSize = selectedOption.value;
    setEditorState({
      ...editorState,
      formatting: {
        ...editorState.formatting,
        fontSize,
      },
    });
    document.execCommand('fontSize', false, fontSize);
  };

  const changeFontFamily = (selectedOption) => {
    const fontFamilyValue = selectedOption.value;
    setEditorState({
      ...editorState,
      fontFamily: fontFamilyValue,
    });
    editableElementRef?.current?.focus?.();
    document.execCommand('fontName', false, fontFamilyValue);
  };

  const handleTextAlignChange = (selectedOption) => {
    setEditorState({
      ...editorState,
      formatting: {
        ...editorState.formatting,
        align: selectedOption.value,
      },
    });
    const alignment = {
      left: 'justifyLeft',
      center: 'justifyCenter',
      right: 'justifyRight',
      justify: 'justifyFull',
    }[selectedOption.value];

    editableElementRef?.current?.focus?.();
    document.execCommand(alignment, false, selectedOption.value); // option for alignment (justifyFull, justifyCenter, justifyLeft, justifyRight)
  };

  const saveState = () => {
    const content = editableElementRef.current.innerHTML;
    setEditorState((prevState) => ({
      ...prevState,
      currentStateIndex: prevState.currentStateIndex + 1,
      currentState: [...prevState.currentState.slice(0, prevState.currentStateIndex + 1), content],
      content: content, // Update content state
    }));
  };
  const saveStateF = (event) => {
    const content = event.target.innerHTML;

    setEditorState((prevState) => ({
      ...prevState,
      currentStateIndex: prevState.currentStateIndex + 1,
      currentState: [...prevState.currentState.slice(0, prevState.currentStateIndex + 1), content],
      content: content, // Update content state
    }));
  };

  const handleClickOutside = (event) => {
    if (
      elementRef.current &&
      !elementRef?.current?.contains?.(event.target) &&
      !modalRef?.current &&
      !modalRef?.current?.contains?.(event.target) &&
      Object.keys(editableElementRef.current).length > 0 &&
      editableElementRef.current &&
      !editableElementRef?.current?.contains?.(event.target)
    ) {
      editableElementRef.current.blur();
      editableElementRef.current = {};
      setBook(bookRef.current);
    }
    if (!event.target?.classList?.contains?.('block-adder')) {
      setShowBlock('');
    }

    if (modalRef?.current && !modalRef?.current?.contains?.(event.target)) {
      setIsOpen(false); // Close the dropdown
      setUrl('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <ModalPortal>
        <div
          className='modal overflow-hidden'
          style={{ display: isOpen ? 'block' : 'none' }}
          tabIndex='-1'
          aria-labelledby='select_an_optionLabel'
          aria-hidden='true'
        >
          <div className='modal-dialog modal-dialog-centered ml-35 overflow-hidden' ref={modalRef}>
            <div className='modal-content'>
              <div className='d-flex flex-column align-items-start p-20 gap-4 link-container'>
                <div className='d-flex flex-column align-items-start gap-12 w-100'>
                  <h3 className='p-0 m-0 link-header'>Add a link</h3>
                  <div className='d-flex flex-column gap-2 w-100'>
                    <img
                      src='/assets/images/cross.png'
                      alt=''
                      width={20}
                      height={20}
                      className='cursor position-absolute me-2'
                      style={{ right: '24px', bottom: '83px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setUrl('');
                      }}
                    />
                    <input
                      type='text'
                      className='link-input-modal w-100'
                      placeholder='Paste the link...'
                      // onFocus={(e)=>{e.stopPropagation();e.preventDefault();}}
                      value={url}
                      onChange={handleChange}
                    />
                    {/* <p className="m-0 p-0 text-red ps-1 fs-11" style={{ height: "10px"}}>{!isValid ? "Invalid Url": ""}</p> */}
                  </div>
                </div>
                <div className='d-flex justify-content-between w-100'>
                  <button className='border-0 bg-transparent link-clx' onClick={handleRemoveLink}>
                    Remove Link
                  </button>
                  <button
                    className='cursor save-btn rounded-0 link-save text-decoration-none'
                    onClick={handleClick}
                  >
                    Save --&gt;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>
      <div
        className='watch'
        style={{
          position: 'fixed',
          width: '75%',
          zIndex: 4,
          backgroundColor: '#fff',
        }}
        ref={elementRef}
      >
        <EditorToolbarFun
          undo={undo}
          redo={redo}
          bookId={bookId}
          createLink={createLink}
          editorState={editorState}
          toggleOrders={toggleOrders}
          toggleBullets={toggleBullets}
          changeFontSize={changeFontSize}
          setSelectOption={setSelectOption}
          changeFontFamily={changeFontFamily}
          updateFormatting={updateFormatting}
          editableElementRef={editableElementRef}
          handleTextAlignChange={handleTextAlignChange}
          style={{
            paddingbottom: '10px',
          }}
        />
      </div>
    </>
  );
};
